import {FormSelectOption} from "./form-select-option";
import {BehaviorSubject, Observable, Subject, Subscription} from "rxjs";
import {isDefined, nonBlank, notDefined, unsubscribe} from "../../utils/epic.utils";

export class FormMetadata {

  public readonly name: string;
  public translation: string;
  public translationSub: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);
  public selectOptions: FormSelectOption[] = [];
  public asyncSelectOptions: ((input: any) => Observable<FormSelectOption[]>) | undefined;
  public asyncSelectOptionById: ((id: string) => Observable<FormSelectOption | undefined>) | undefined;
  public defaultOption: FormSelectOption | undefined;
  public info: string | undefined;
  public infoFn: ((value: string) => (string | undefined)) | undefined;
  public infoTranslated: boolean = false;
  public help: string | undefined;
  public warn: string | undefined;
  public warnFn: ((value: string) => (string | undefined)) | undefined;
  public label: 'show' | 'hide' = 'show';

  private readonly subs: Subscription[] = [];
  private readonly fnDelay: number = 100;
  private readonly accChanged: Subject<void> = new Subject<void>();
  private options: FormSelectOption[] | Observable<FormSelectOption[]> | undefined;
  private infoTimer: any;
  private warnTimer: any;

  constructor(name: string) {
    this.name = name;
    this.translation = name;
    this.setTranslation(name);
  }

  public destroy(): void {
    unsubscribe(this.subs);
  }

  public setTranslation(translation: string): FormMetadata {
    this.translation = translation;
    this.translationSub.next(translation);
    return this;
  }

  public setSelectOption(options: FormSelectOption[] | Observable<FormSelectOption[]>): FormMetadata {
    this.options = options;
    return this;
  }

  public setAsyncSelectOption(fn: (input: any) => Observable<FormSelectOption[]>): FormMetadata {
    this.asyncSelectOptions = fn;
    return this;
  }

  // Resolves a single option by its id explicitly, so the init value can be loaded
  // even if it is not part of the (limited) async typeahead result list.
  public setAsyncSelectOptionById(fn: (id: string) => Observable<FormSelectOption | undefined>): FormMetadata {
    this.asyncSelectOptionById = fn;
    return this;
  }

  public setDefaultOption(option: FormSelectOption | undefined): FormMetadata {
    this.defaultOption = option;
    return this;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public setSelectEnum(enm: any, prefix?: string, defaultEnum?: any, excluded?: any[]): FormMetadata {
    const options: FormSelectOption[] = Object.values(enm)
      .filter((e) => notDefined(excluded) || !excluded.includes(e))
      .filter((e) => typeof e === 'string')
      .map((e) => String(e))
      .map((e) => {
        const name: string = e.toLowerCase();
        const fieldName: string = prefix ? prefix + name : name;
        return new FormSelectOption(fieldName, e.toUpperCase());
      });
    if (isDefined(defaultEnum)) {
      this.setDefaultOption(options.find((o) => o.value === defaultEnum));
    }
    return this.setSelectOption(options);
  }

  public hasInfo(): boolean {
    return nonBlank(this.info);
  }

  public setInfo(info: string | undefined, translated: boolean = false): FormMetadata {
    this.info = info;
    this.infoTranslated = translated;
    return this;
  }

  public setInfoFn(fn: (value: string) => (string | undefined), translated: boolean = false): FormMetadata {
    this.infoFn = fn;
    this.infoTranslated = translated;
    return this;
  }

  public isInfoTranslated(): boolean {
    return this.infoTranslated;
  }

  public updateInfo(value: string): void {
    if (isDefined(this.infoFn)) {
      clearTimeout(this.infoTimer);
      this.infoTimer = setTimeout(() => this.info = this.infoFn!(value), this.fnDelay);
      this.subs.push(this.infoTimer);
    }
  }

  public setHelp(help: string): FormMetadata {
    this.help = help;
    return this;
  }

  public hasWarn(): boolean {
    return nonBlank(this.warn);
  }

  // noinspection JSUnusedGlobalSymbols
  public setWarn(warn: string): FormMetadata {
    this.warn = warn;
    return this;
  }

  public setWarnFn(fn: (value: string) => string | undefined): FormMetadata {
    this.warnFn = fn;
    return this;
  }

  public updateWarn(value: string): void {
    if (isDefined(this.warnFn)) {
      clearTimeout(this.warnTimer);
      this.warnTimer = setTimeout(() => this.warn = this.warnFn!(value), this.fnDelay);
      this.subs.push(this.warnTimer);
    }
  }

  public hideLabel(): FormMetadata {
    this.label = 'hide';
    return this;
  }

  public loadSelectOptions(): Observable<boolean> {
    return new Observable<boolean>((obs) => {
      if (isDefined(this.options) && this.options instanceof Observable) {
        this.subs.push(this.options.subscribe({
          next: (res) => {
            this.selectOptions = res;
            obs.next(true);
            obs.complete();
          },
          error: (ex) => {
            obs.error(ex);
            obs.complete();
          },
          complete: () => obs.complete()
        }));
      } else {
        if (isDefined(this.options)) {
          this.selectOptions = this.options;
        }
        obs.next(true);
        obs.complete();
      }
    });
  }

  public changed(): void {
    this.accChanged.next();
  }

  public onChanged(): Observable<void> {
    return this.accChanged.asObservable();
  }
}

